import React, { useState, useEffect } from 'react';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl } from '../../baseurl';
import { Coins, Search, Edit2, X, ArrowUpRight, ArrowDownLeft, ShieldCheck, FileText, History } from 'lucide-react';

const Credits = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // History Log States
  const [selectedWalletForHistory, setSelectedWalletForHistory] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Modal Adjustment States
  const [showModal, setShowModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState('add'); // 'add' or 'deduct'
  const [creditAmount, setCreditAmount] = useState('');
  const [transactionType, setTransactionType] = useState('bonus'); // 'bonus', 'purchase', 'refund', 'usage'
  const [reasonDescription, setReasonDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const adminToken = localStorage.getItem('adminToken');

  // Fetch active circulating wallet structures
  const fetchCreditsData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}getAllUserCreditsForAdmin`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (response.data.success) {
        setWallets(response.data.credits);
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Fetch Failed', text: 'Could not fetch ledger details.' });
    } finally {
      setTimeout(() => setLoading(false), 400);
    }
  };

  // Fetch transaction historical record sets for a single wallet user context
  const fetchWalletHistory = async (wallet) => {
    setSelectedWalletForHistory(wallet);
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${baseurl}getWalletHistoryAdmin/${wallet.credit_wallet_id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      console.log("response", response);
      if (response.data.success) {
        setHistoryLogs(response.data.history);
      }
    } catch (err) {
      console.error(err);
      setHistoryLogs([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchCreditsData();
  }, [adminToken]);

  const handleOpenAdjustmentModal = (wallet) => {
    setSelectedWallet(wallet);
    setAdjustmentType('add');
    setCreditAmount('');
    setTransactionType('bonus');
    setReasonDescription('');
    setShowModal(true);
  };

  const handleUpdateCreditsSubmit = async (e) => {
    e.preventDefault();
    if (!creditAmount || parseFloat(creditAmount) <= 0) {
      Swal.fire('Validation Error', 'Please input a valid numeric amount greater than 0.', 'warning');
      return;
    }

    const finalAmount = adjustmentType === 'deduct' 
      ? -Math.abs(parseFloat(creditAmount)) 
      : Math.abs(parseFloat(creditAmount));

    setSubmitting(true);
    try {
      // Endpoint trailing slashes explicitly mapped out securely here
      const response = await axios.post(`${baseurl}updateUserCreditsAdminNew`, {
        userId: selectedWallet.user_id,
        walletId: selectedWallet.credit_wallet_id,
        amount: finalAmount,
        transactionType: transactionType,
        description: reasonDescription
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      if (response.data.success) {
        Swal.fire('Balance Adjusted!', response.data.message, 'success');
        setShowModal(false);
        fetchCreditsData();
        // Live reload log context sync panel safely if open
        if (selectedWalletForHistory?.credit_wallet_id === selectedWallet.credit_wallet_id) {
          fetchWalletHistory(selectedWallet);
        }
      }
    } catch (err) {
      console.error("--- CREDIT ADJUSTMENT FAILURE ---", err.response?.data);
      const serverErrorMessage = err.response?.data?.message || err.response?.data?.error;
      Swal.fire('Adjustment Failed', serverErrorMessage || 'Network connection issues encountered.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredWallets = wallets.filter(w => 
    `${w.full_name} ${w.email} ${w.company_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalSystemBalance = filteredWallets.reduce((acc, curr) => acc + parseFloat(curr.balance || 0), 0);
  const totalSystemEarned = filteredWallets.reduce((acc, curr) => acc + parseFloat(curr.total_earned || 0), 0);
  const currentRows = filteredWallets.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex', minHeight: '100vh' }}>
        <SideMenu />
        
        <div className="profile-content-container p-4" style={{ flex: 1, overflow: 'auto' }}>
          
          {/* HEADER ROW */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <h2 className="fw-bold mb-1" style={{ color: '#1B2559' }}>User Credit System</h2>
              <p className="text-muted m-0">Monitor wallet balances, adjust parameters, and review system liability profiles.</p>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <div className="position-relative">
                <Search className="position-absolute top-50 translate-middle-y text-muted ms-3" size={16} />
                <input 
                  type="text" 
                  className="form-control rounded-pill ps-5 bg-white border shadow-sm" 
                  placeholder="Search user, email, company..." 
                  style={{ width: '280px', fontSize: '14px' }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* AGGREGATED CARDS */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm p-3 bg-white" style={{ borderRadius: '15px' }}>
                <div className="d-flex align-items-center gap-3">
                  <div className="p-3 rounded-circle" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                    <Coins size={24} />
                  </div>
                  <div>
                    <span className="text-muted small d-block fw-semibold uppercase">Active Circulating Balance</span>
                    <h3 className="fw-bold m-0" style={{ color: '#1B2559' }}>${totalSystemBalance.toFixed(2)}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm p-3 bg-white" style={{ borderRadius: '15px' }}>
                <div className="d-flex align-items-center gap-3">
                  <div className="p-3 rounded-circle" style={{ background: '#E3F2FD', color: '#1565C0' }}>
                    <ArrowUpRight size={24} />
                  </div>
                  <div>
                    <span className="text-muted small d-block fw-semibold uppercase">Total Cumulative Issuance</span>
                    <h3 className="fw-bold m-0" style={{ color: '#1B2559' }}>${totalSystemEarned.toFixed(2)}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm p-3 bg-white" style={{ borderRadius: '15px' }}>
                <div className="d-flex align-items-center gap-3">
                  <div className="p-3 rounded-circle" style={{ background: '#FFF3E0', color: '#EF6C00' }}>
                    <ShieldCheck size={24} />
                  </div>
                  <div>
                    <span className="text-muted small d-block fw-semibold uppercase">Accounts Tracked</span>
                    <h3 className="fw-bold m-0" style={{ color: '#1B2559' }}>{filteredWallets.length} Users</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SPLIT VIEW LAYOUT: MAIN LEDGER AND SELECTABLE WALLET HISTORY LOG */}
          <div className="row g-4">
            <div className={selectedWalletForHistory ? "col-lg-7" : "col-lg-12"} style={{ transition: 'all 0.3s ease' }}>
              <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4 text-muted small fw-bold">USER ID</th>
                        <th className="text-muted small fw-bold">CLIENT INFORMATION</th>
                        <th className="text-muted small fw-bold text-end">AVAILABLE BALANCE</th>
                        <th className="text-muted small fw-bold text-center">TOTAL EARNED / SPENT</th>
                        <th className="text-center text-muted small fw-bold">ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan="5" className="p-5 text-center text-muted">Synchronizing account states...</td></tr>
                      ) : currentRows.length === 0 ? (
                        <tr><td colSpan="5" className="p-5 text-center text-muted">No wallets matched parameters.</td></tr>
                      ) : (
                        currentRows.map((wallet) => (
                          <tr 
                            key={wallet.credit_wallet_id} 
                            style={{ cursor: 'pointer' }}
                            className={selectedWalletForHistory?.credit_wallet_id === wallet.credit_wallet_id ? 'table-primary bg-opacity-10' : ''}
                            onClick={() => fetchWalletHistory(wallet)}
                          >
                            <td className="ps-4 text-muted">#USR-{wallet.user_id}</td>
                            <td>
                              <div className="fw-bold text-dark">{wallet.full_name}</div>
                              <div className="small text-muted" style={{ fontSize: '12px' }}>{wallet.email}</div>
                              <div className="text-primary" style={{ fontSize: '11px', fontWeight: 600 }}>{wallet.company_name}</div>
                            </td>
                            <td className="text-end font-monospace fw-bold pe-3">
                              <span style={{ color: parseFloat(wallet.balance) >= 0 ? '#2E7D32' : '#C62828', fontSize: '14px' }}>
                                ${parseFloat(wallet.balance || 0).toFixed(2)}
                              </span>
                            </td>
                            <td className="text-center font-monospace text-muted small">
                              <span className="text-success">+${parseFloat(wallet.total_earned || 0).toFixed(2)}</span>
                              <span className="mx-1">|</span>
                              <span className="text-danger">-${parseFloat(wallet.total_spent || 0).toFixed(2)}</span>
                            </td>
                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                              <button 
                                onClick={() => handleOpenAdjustmentModal(wallet)} 
                                className="btn btn-outline-primary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-1"
                                style={{ fontSize: '12px', fontWeight: 500 }}
                              >
                                <Edit2 size={12} /> Adjust
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* HISTORICAL LEDGER LOG PANEL (POPS OUT SIDEWAYS) */}
            {selectedWalletForHistory && (
              <div className="col-lg-5">
                <div className="card border-0 shadow-sm p-4 bg-white position-relative" style={{ borderRadius: '15px', minHeight: '400px' }}>
                  <button 
                    className="position-absolute btn btn-light rounded-circle p-1" 
                    style={{ top: '15px', right: '15px', border: 'none' }}
                    onClick={() => setSelectedWalletForHistory(null)}
                  >
                    <X size={18} className="text-muted" />
                  </button>
                  
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <History className="text-primary" size={20} />
                    <h5 className="fw-bold m-0" style={{ color: '#1B2559' }}>Transaction History</h5>
                  </div>
                  <p className="text-muted small mb-4">Viewing audit tracks for <strong>{selectedWalletForHistory.full_name}</strong></p>

                  <div className="history-scroll-panel" style={{ maxHeight: '480px', overflowY: 'auto' }}>
                    {loadingHistory ? (
                      <div className="text-center py-5 text-muted small">Loading records loop...</div>
                    ) : historyLogs.length === 0 ? (
                      <div className="text-center py-5 text-muted small bg-light rounded-3">No historical ledger modifications logged for this account wallet.</div>
                    ) : (
                      <div className="d-flex flex-column gap-3">
                        {historyLogs.map((log) => {
                          const isPositive = parseFloat(log.amount) >= 0;
                          return (
                            <div key={log.id} className="p-3 border rounded-3 bg-white shadow-xs d-flex justify-content-between align-items-start">
                              <div style={{ maxWidth: '75%' }}>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <span className={`badge rounded-pill ${
                                    log.transaction_type === 'purchase' ? 'bg-primary' : 
                                    log.transaction_type === 'usage' ? 'bg-secondary' : 
                                    log.transaction_type === 'refund' ? 'bg-info' : 'bg-warning text-dark'
                                  }`} style={{ fontSize: '9px' }}>
                                    {log.transaction_type}
                                  </span>
                                  <span className="text-muted" style={{ fontSize: '11px' }}>
                                    {new Date(log.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                                  </span>
                                </div>
                                <p className="mb-0 text-dark small fw-medium">{log.description || 'No descriptor note provided.'}</p>
                              </div>
                              <div className="text-end font-monospace fw-bold" style={{ color: isPositive ? '#2E7D32' : '#C62828' }}>
                                {isPositive ? '+' : ''}${parseFloat(log.amount).toFixed(2)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ADJUSTMENT MODAL */}
      {showModal && (
        <div className="modal-backdrop-custom">
          <div className="modal-card shadow-lg">
            <div className="modal-header-custom">
              <div>
                <h5 className="m-0 fw-bold" style={{ color: '#1B2559' }}>Manual Balance Adjustment</h5>
                <span className="small text-muted">Modifying ledger configurations for {selectedWallet?.full_name}</span>
              </div>
              <button className="btn-close-custom" onClick={() => setShowModal(false)}><X size={20}/></button>
            </div>
            
            <form onSubmit={handleUpdateCreditsSubmit}>
              <div className="modal-body-custom">
                <div className="ticket-info-box mb-4 d-flex justify-content-between align-items-center">
                  <div>
                    <label className="text-primary small fw-bold text-uppercase d-block mb-1">Current Stored Assets</label>
                    <p className="mb-0 text-muted small">{selectedWallet?.company_name}</p>
                  </div>
                  <h3 className="font-monospace fw-bold m-0 text-primary">
                    ${parseFloat(selectedWallet?.balance || 0).toFixed(2)}
                  </h3>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">Action Type</label>
                  <div className="d-flex gap-2">
                    <button 
                      type="button"
                      className={`btn w-50 rounded-pill d-flex align-items-center justify-content-center gap-2 ${adjustmentType === 'add' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setAdjustmentType('add')}
                    >
                      <ArrowUpRight size={16} /> Load / Add Credits
                    </button>
                    <button 
                      type="button"
                      className={`btn w-50 rounded-pill d-flex align-items-center justify-content-center gap-2 ${adjustmentType === 'deduct' ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => setAdjustmentType('deduct')}
                    >
                      <ArrowDownLeft size={16} /> Charge / Deduct
                    </button>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold text-muted text-uppercase">Credit Amount ($)</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0 font-monospace">$</span>
                      <input 
                        type="number" 
                        step="0.01"
                        min="0.01"
                        required
                        className="form-control ps-2" 
                        placeholder="0.00"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label small fw-bold text-muted text-uppercase">Ledger Allocation Tag</label>
                    <select className="form-select text-capitalize" value={transactionType} onChange={(e) => setTransactionType(e.target.value)}>
                      <option value="bonus">bonus (Trial / Campaign)</option>
                      <option value="purchase">purchase (Direct Checkout)</option>
                      <option value="usage">usage (Report processing charge)</option>
                      <option value="refund">refund (Service reversal correction)</option>
                    </select>
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label small fw-bold text-muted text-uppercase">Audit Reason / Description</label>
                  <div className="position-relative">
                    <FileText className="position-absolute top-0 start-0 mt-2 ms-3 text-muted" size={16} />
                    <textarea 
                      className="form-control ps-5" 
                      rows="3" 
                      required
                      maxLength="255"
                      placeholder="Provide reasoning for auditing transparency records..."
                      value={reasonDescription}
                      onChange={(e) => setReasonDescription(e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </div>

              <div className="modal-footer-custom">
                <button type="button" className="btn btn-light rounded-pill px-4" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={submitting}>
                  {submitting ? 'Applying Ledger Delta...' : 'Save Credit Adjustments'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>
        {`
        .modal-backdrop-custom {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(18, 24, 59, 0.4); backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center; z-index: 9999;
        }
        .modal-card {
          background: white; width: 560px; border-radius: 20px;
          overflow: hidden; animation: zoomInEffect 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes zoomInEffect { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .modal-header-custom { padding: 20px 24px; border-bottom: 1px solid #f1f3f9; display: flex; justify-content: space-between; align-items: center; }
        .modal-body-custom { padding: 24px; }
        .modal-footer-custom { padding: 15px 24px; background: #f8fafe; display: flex; justify-content: flex-end; gap: 10px; border-top: 1px solid #f1f3f9; }
        .ticket-info-box { background: #f3f6ff; padding: 16px 20px; border-radius: 14px; border-left: 5px solid #4318FF; }
        .btn-close-custom { background: none; border: none; color: #a3aed0; cursor: pointer; transition: color 0.15s; }
        .btn-close-custom:hover { color: #1b2559; }
        .uppercase { text-transform: uppercase; }
        .history-scroll-panel::-webkit-scrollbar { width: 5px; }
        .history-scroll-panel::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; }
        `}
      </style>
    </div>
  );
};

export default Credits;